import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import usePresentPage, {
  selectors as pageSelectors,
} from 'client/hooks/usePresentPage'
import { isWebsitePageType } from 'client/store/page/pageSelectors'
import { pageTypes } from 'common/constants/pageTypes'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { blockCategories, blockCategoryNames } from '../consts'
import { BlockCategoryType, BlockType } from '../types'
import { BlockCategory } from './block-category'

const blockCategoryIcons: Partial<
  Record<BlockCategoryType, React.JSX.Element>
> = {
  [blockCategories.optInForms]: <i className="fa-light fa-file-check" />,
  [blockCategories.features]: <i className="fa-light fa-thumbs-up" />,
  [blockCategories.teamMembers]: <i className="fa-light fa-users" />,
  [blockCategories.orderForms]: <i className="fa-light fa-cart-shopping" />,
  [blockCategories.hero]: <i className="fa-light fa-shopping-cart" />,
  [blockCategories.pricePlans]: <i className="fa-light fa-user" />,
  [blockCategories.pageHeaders]: <i className="fa-light fa-user" />,
  [blockCategories.pageFooters]: <i className="fa-light fa-down-to-line" />,
  [blockCategories.testimonials]: <i className="fa-light fa-message-text" />,
}

interface BlockCategoriesProps {
  pageType: PageTypeEnum
  setActiveCategory: (category: BlockCategoryType) => void
}

export const BlockCategories = ({
  pageType,
  setActiveCategory,
}: BlockCategoriesProps) => {
  const { t } = useTranslation()
  const commonBlocksByCategories: Record<BlockCategoryType, BlockType[]> =
    useBlocks(blocksSelectors.getCommonBlocksByCategories)

  const isOfferPageOrNotOrderFormCategory = (category: string) =>
    pageType === pageTypes.offerPage || category !== blockCategories.orderForms

  const isTemplate = usePresentPage(pageSelectors.isTemplate)
  const userBlocks: BlockType[] = useBlocks(blocksSelectors.getUserBlocks)
  const masterBlocks: BlockType[] = useBlocks(blocksSelectors.getMasterBlocks)
  const isWebsitePage = usePresentPage(isWebsitePageType)

  // no common blocks allowed for website pages except for those created by user himself
  const allowedCommonCategories = isWebsitePage
    ? []
    : Object.keys(commonBlocksByCategories)
        .filter(isOfferPageOrNotOrderFormCategory)
        .map(category => ({
          name: t(blockCategoryNames[category as BlockCategoryType]),
          type: category as BlockCategoryType,
          icon: blockCategoryIcons[category as BlockCategoryType],
        }))

  const isMasterBlockAvailable = useMemo(() => {
    return !isTemplate && masterBlocks.length > 0 && !isWebsitePage
  }, [isTemplate, masterBlocks.length, isWebsitePage])
  const isUserBlockAvailable = useMemo(() => {
    return userBlocks.length > 0
  }, [userBlocks.length])
  const allowedBlockAvailable = useMemo(() => {
    return allowedCommonCategories.length > 0
  }, [allowedCommonCategories.length])

  const hasAnyBlocks: boolean =
    isMasterBlockAvailable || isUserBlockAvailable || allowedBlockAvailable

  return (
    <div>
      {!hasAnyBlocks && (
        <div className="mt-12 text-center text-textTertiary">
          <span className="text-S">{t('components.core.blocks.empty')}</span>
        </div>
      )}

      {isMasterBlockAvailable && (
        <BlockCategory
          label={t(blockCategoryNames.userMaster)}
          onClick={() => setActiveCategory(blockCategories.userMaster)}
          icon={<i className="fa-light fa-bolt" />}
        />
      )}
      {isUserBlockAvailable && (
        <BlockCategory
          label={t(blockCategoryNames.userSimple)}
          onClick={() => setActiveCategory(blockCategories.userSimple)}
          icon={<i className="fa-light fa-floppy-disk" />}
        />
      )}
      {allowedBlockAvailable &&
        allowedCommonCategories.map(category => (
          <BlockCategory
            key={category.type}
            label={category.name}
            onClick={() => setActiveCategory(category.type)}
            icon={category.icon}
          />
        ))}
    </div>
  )
}
